import {Component, ChangeDetectionStrategy, OnInit, TemplateRef, ViewChild, Input} from '@angular/core';
import {
  CalendarEvent,
  CalendarMonthViewDay,
  CalendarView,
  CalendarEventTimesChangedEvent,
  collapseAnimation,
} from 'angular-calendar';

import { isSameMonth, isSameDay } from 'date-fns';
import { colors } from './utils/colors';
import { EventsCalendarService } from 'src/app/core/services/events-calendar/events-calendar.service';
import { EventsCalendarStore } from 'src/app/core/services/events-calendar/events-calendar.store';
import { Subject } from 'rxjs';
import { AuthService } from 'src/app/core/services/user-services/auth.service';
import {CalendarioEventiPrivati} from '../../models/calendario-eventi-privati';
import {MatDialog} from '@angular/material/dialog';
import {ShowPortfoliosDialogComponent} from './show-portfolios-dialog/show-portfolios-dialog.component';
import {Router} from '@angular/router';

interface EventGroupMeta {
  type: string;
}

class EventGroup {
  constructor(date: Date) {
    this.date = date;
    this.eventsDetails = {
      'Data strike: ': 0,
      'Emissione: ': 0,
      'Scadenza: ': 0,
      'Rilevazione: ': 0,
      'Fine collocamento: ': 0,
      'Valuation date: ': 0,
      'Personal: ': 0
    };
  }

  date: Date;
  eventsDetails: { [key: string]: number };
}

interface EventsDetails {
  eventsDetails: { [key: string]: string };
}

@Component({
  selector: 'app-view-all-events-calendar-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './view-all-events-calendar.component.html',
  styleUrls: ['./view-all-events-calendar.component.scss'],
  animations: [collapseAnimation]
})
export class ViewAllEventsCalendarComponent implements OnInit {
  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any>;
  @Input() isLandingPage = false;

  constructor(
    private service: EventsCalendarService,
    public store: EventsCalendarStore,
    private authService: AuthService,
    private dialog: MatDialog,
    private router: Router) {
  }

  view: CalendarView = CalendarView.Month;
  activeDayIsOpen = this.store.records.filter(e =>
    new Date(e.dataevento).getTime() === new Date().getTime()).length > 0;
  refresh: Subject<any> = new Subject();
  modalData: {
    action: string;
    event: CalendarEvent;
  };
  isDetailToggled = [];

  viewDate: Date = new Date();

  events: CalendarEvent[] = [];

  groupedSimilarEvents: CalendarEvent[] = [];

  isGuest: boolean;

  ngOnInit() {
    this.isGuest = this.authService.user?.profile?.role === 'CedLabGuest';

    if (!this.isGuest || !this.isLandingPage) {
        this.getRecords(new Date());
    }
  }

  getEventsGroups(date: Date) {
    const eventsGroups: EventGroup[] = [];

    this.store.records.forEach(element => {
      let eventsGroup = eventsGroups.find(ev =>
        new Date(ev.date).getTime() === new Date(element.dataevento).getTime()
      );

      if (!eventsGroup) {
        eventsGroup = new EventGroup(new Date(element.dataevento));
        eventsGroups.push(eventsGroup);
      }

      const eventIndex = eventsGroups.indexOf(eventsGroup);

      switch (element.causale) {
        case 'data_strike':
          ++eventsGroups[eventIndex].eventsDetails['Data strike: '];
          break;
        case 'emissione':
          ++eventsGroups[eventIndex].eventsDetails['Emissione: '];
          break;
        case 'scadenza':
          ++eventsGroups[eventIndex].eventsDetails['Scadenza: '];
          break;
        case 'rilevazione':
          ++eventsGroups[eventIndex].eventsDetails['Rilevazione: '];
          break;
        case 'fine_collocamento':
          ++eventsGroups[eventIndex].eventsDetails['Fine collocamento: '];
          break;
        case 'valuation_date':
          ++eventsGroups[eventIndex].eventsDetails['Valuation date: '];
          break;
        default:
          ++eventsGroups[eventIndex].eventsDetails['Other: '];
      }

    });

    this.store.privateRecords.forEach((element: CalendarioEventiPrivati) => {
      const keys = Object.keys(element);
      let eventsGroup = null;
      keys.forEach(key => {
        if (typeof element[key] === 'number' || element[key] === null || isNaN(Date.parse(element[key]))) {
          return;
        }
        if (new Date(element[key]).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) || new Date(element[key]).getMonth() !== date.getMonth()) {
          return;
        } else {
          eventsGroup = eventsGroups.find(ev =>
            new Date(ev.date).setHours(0, 0, 0, 0) === new Date(element[key]).setHours(0, 0, 0, 0)
          );
          if (!eventsGroup) {
            eventsGroup = new EventGroup(new Date(element[key]));
            eventsGroups.push(eventsGroup);
          }

          const eventIndex = eventsGroups.indexOf(eventsGroup);

          ++eventsGroups[eventIndex].eventsDetails['Personal: '];
        }
      });
    }

    );

    eventsGroups.forEach(eg => {
      const keys = Object.keys(eg.eventsDetails);
      keys.forEach(e => {
        if (eg.eventsDetails[e] > 0) {
          this.events.push(
            {
              title: e + eg.eventsDetails[e],
              color: this.setColorByCausale(e),
              start: new Date(eg.date),
              meta: {
                type: this.setMetaTypeByCausale(e),
              }
            });
          }
        });
      });

    this.isDetailToggled = [];
    this.activeDayIsOpen = false;
  }

  getRecords(date: Date) {
    this.store.showLoader = true;
    this.service.get(date).subscribe(
      () => this.getPrivateRecords(date),
      () => this.store.showLoader = false
    );
  }

  getPreviousMonth() {
    this.store.records = [];
    this.events = [];
    this.viewDate = new Date(this.viewDate.setMonth(this.viewDate.getMonth() - 1));
    this.getRecords(this.viewDate);
  }

  getCurrentMonth() {
    if (new Date(this.viewDate).getTime() !== new Date().getTime()) {
      this.store.records = [];
      this.events = [];
      this.viewDate = new Date();
      this.getRecords(this.viewDate);
    }
  }

  getNextMonthRecords() {
    this.store.records = [];
    this.events = [];
    this.viewDate = new Date(this.viewDate.setMonth(this.viewDate.getMonth() + 1));
    this.getRecords(this.viewDate);
  }

  getPrivateRecords(date: Date) {
    this.service.getPrivate(date).subscribe(
      () => {
        this.getEventsGroups(date);
        this.refresh.next();
        this.store.showLoader = false;
      },
      () => this.store.showLoader = false
    );
  }

  dailyEvents(event) {
    if (event.title.toUpperCase().includes('PERSONAL')) {
      const eventsDetails: string[] = [];
      this.store.privateRecords.forEach(element => {
        const keys = Object.keys(element);
        keys.forEach(key => {
          if (new Date(element[key]).getTime() !== new Date(event.start).getTime()) {
            return;
          }
          eventsDetails.push(key + ': ' + element.isin);
        });
      });
      const sortedPrivate = eventsDetails.sort((one, two) => (one > two ? 1 : -1));
      return sortedPrivate;
    }

    let isinList: string[] = [];

    isinList = this.store.records.filter(e =>
      new Date(e.dataevento).getTime() === new Date(event.start).getTime()
      && e.causale === this.getFormattedCausale(event.title)
      ).map(e => e.codiceIsin);
    const sortedPublic = isinList.sort((one, two) => (one > two ? 1 : -1));
    return sortedPublic;
  }

  getFormattedCausale(causale: string) {
    if (causale.includes('Data strike: ')) {
      return 'data_strike: ';
    } else if (causale.includes('Emissione: ')) {
      return 'emissione';
    } else if (causale.includes('Scadenza: ')) {
      return 'scadenza';
    } else if (causale.includes('Rilevazione: ')) {
      return 'rilevazione';
    } else if (causale.includes('Fine collocamento: ')) {
      return 'fine_collocamento';
    } else if (causale.includes('Valuation date: ')) {
      return 'valuation_date';
    }
  }

  setChipsBackgroundColorByCausale(causale: string) {
    if (causale.toUpperCase().includes('Data strike: '.toUpperCase())) {
      return colors.yellow.primary;
    } else if (causale.toUpperCase().includes('Emissione: '.toUpperCase())) {
      return colors.green.primary;
    } else if (causale.toUpperCase().includes('Scadenza: '.toUpperCase())) {
      return colors.red.primary;
    } else if (causale.toUpperCase().includes('Rilevazione: '.toUpperCase())) {
      return colors.orange.primary;
    } else if (causale.toUpperCase().includes('Fine collocamento: '.toUpperCase())) {
      return colors.purple.primary;
    } else if (causale.toUpperCase().includes('Valuation date: '.toUpperCase())) {
      return colors.lightGreen.primary;
    } else if (causale.toUpperCase().includes('Personal: '.toUpperCase())) {
      return colors.green.primary;
    } else {
      return colors.darkYellow.primary;
    }
  }

  setColorByCausale(causale: string) {
    switch (causale) {
      case 'Data strike: ':
        return colors.yellow;
      case 'Emissione: ':
        return colors.green;
      case 'Scadenza: ':
        return colors.red;
      case 'Rilevazione: ':
        return colors.orange;
      case 'Fine collocamento: ':
        return colors.purple;
      case 'Valuation date: ':
        return colors.lightGreen;
      case 'Personal: ':
        return colors.green;
      default:
        return colors.darkYellow;
    }
  }

  setMetaTypeByCausale(causale: string) {
    switch (causale) {
      case 'Data strike: ':
        return 'warning';
      case 'Emissione: ':
        return 'warning';
      case 'Scadenza: ':
        return 'danger';
      case 'Rilevazione: ':
        return 'info';
      case 'Fine collocamento: ':
        return 'warning';
      case 'Valuation date: ':
        return 'info';
      case 'Personal: ':
        return 'info';
      default:
        return 'info';
    }
  }

  beforeMonthViewRender({
    body,
  }: {
    body: CalendarMonthViewDay<EventGroupMeta>[];
  }): void {
    // month view has a different UX from the week and day view so we only really need to group by the type
    body.forEach((cell) => {
      const groups = {};
      cell.events.forEach((event: CalendarEvent<EventGroupMeta>) => {
        groups[event.meta.type] = groups[event.meta.type] || [];
        groups[event.meta.type].push(event);
      });
      // cell.eventGroups = Object.entries(groups);
    });
  }

  printEventsTotal(obj) {
    const total = this.store.records.filter(
      ev => new Date(ev.dataevento).getTime() === new Date(obj.date).getTime()).length;
    return total;
  }

  printPrivateEventsTotal(obj) {
    let total = 0;
    this.store.privateRecords.forEach(privateEvent => {
      const keys = Object.keys(privateEvent);
      keys.forEach(key => {
        if (!(new Date(privateEvent[key]).getTime() === new Date(obj.date).getTime())) {
          return;
        } else {
          ++total;
        }
      });
    });
    return total;
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      this.isDetailToggled = [];
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    }
  }

  eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
  }

  handleEvent(event, index: number): void {
    // this.modalData = { event, action };
    // this.modal.open(this.modalContent, { size: 'lg' });
    if (!this.isDetailToggled[index]) {
      this.isDetailToggled = [];
    }
    this.isDetailToggled[index] = !(this.isDetailToggled[index]);
  }

  getIsinStringForSchedaCertificato(stringData: string) {
    const indexSymbolBeforeIsin = stringData.indexOf(':');
    if (indexSymbolBeforeIsin === -1) {
      return stringData;
    }

    const isinStartIndex = indexSymbolBeforeIsin + 2;
    const isinString = stringData.substring(isinStartIndex);

    return isinString;
  }

  onShowPortfolios(record: string) {
    const isin = this.getIsinStringForSchedaCertificato(record);
    const dialogRef = this.dialog.open(ShowPortfoliosDialogComponent, {
      data: { isin }
    });

    dialogRef.afterClosed().subscribe((selectedPortfolio: {id: number, isin: string, isinState: number}) => {
      if (selectedPortfolio) {
        this.router.navigateByUrl('/menu/portafogli', {state: { selectedPortfolio }});
      }
    });
  }
}
